import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Button,
	TextField,
	FormControlLabel,
	Switch,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	ICreateAIModelModalProps,
	ICreateAIModelRequest,
} from 'src/features/ai_model/domain/types';
import { CreateUpdateAIModelValidationSchema } from 'src/features/ai_model/domain/validation';
import {
	AI_MODEL_LOADING_KEY,
	createAIModel,
} from 'src/features/ai_model/store';
import { AppDispatch, RootState } from 'src/store';

const CreateAIModelModal: React.FC<ICreateAIModelModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
}) => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(
		(state: RootState) => state.loading.loadingStates[AI_MODEL_LOADING_KEY],
	);

	const {
		control,
		reset,
		handleSubmit,
		formState: { errors, isDirty },
	} = useForm<ICreateAIModelRequest>({
		resolver: yupResolver(CreateUpdateAIModelValidationSchema),
		defaultValues: {
			name: '',
			description: '',
			active: true,
			inference_endpoint: '',
			liveness_endpoint: '',
			batch_inference_endpoint: '',
		},
	});

	const handleSubmitClick = async (data: ICreateAIModelRequest) => {
		if (!siteSlug) return;

		dispatch(
			createAIModel({
				request: data,
				siteSlug: siteSlug,
				onSuccess: () => {
					onSuccess();
					snackbar('AI model created successfully', 'success');
					reset();
				},
			}),
		);
	};

	const handleDialogClose = () => {
		if (isLoading) return;
		reset();
		onClose();
	};

	if (!isOpen) return null;

	return (
		<Dialog
			open={isOpen}
			PaperProps={{ sx: { width: 610, minHeight: 190 } }}
			onClose={(event, reason) => {
				if (
					isLoading &&
					(reason === 'backdropClick' || reason === 'escapeKeyDown')
				)
					return;
				handleDialogClose();
			}}
			disableEscapeKeyDown={isLoading}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginTop: '8px',
					pb: 0,
				}}
			>
				<Typography
					sx={{
						fontSize: '20px',
						color: '#000000DE',
						fontWeight: 700,
					}}
				>
					Add AI Model
				</Typography>
				<IconButton
					onClick={handleDialogClose}
					sx={{ p: 0.5 }}
					disabled={isLoading}
				>
					<CloseIcon
						fontSize='small'
						sx={{ color: '#000000DE', fontWeight: 900 }}
					/>
				</IconButton>
			</DialogTitle>

			<DialogContent>
				<form
					onSubmit={handleSubmit(handleSubmitClick)}
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '1rem',
						paddingTop: '1rem',
					}}
				>
					<Controller
						name='name'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Name'
								variant='outlined'
								fullWidth
								error={!!errors.name}
								helperText={errors.name?.message}
								inputProps={{ maxLength: 255 }}
								disabled={isLoading}
							/>
						)}
					/>

					<Controller
						name='description'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Description'
								variant='outlined'
								fullWidth
								multiline
								rows={3}
								error={!!errors.description}
								helperText={errors.description?.message}
								inputProps={{ maxLength: 255 }}
								disabled={isLoading}
							/>
						)}
					/>

					<Controller
						name='active'
						control={control}
						render={({ field }) => (
							<FormControlLabel
								control={
									<Switch
										checked={field.value}
										onChange={(e) =>
											field.onChange(e.target.checked)
										}
										disabled={isLoading}
									/>
								}
								label='Active'
							/>
						)}
					/>

					<Controller
						name='inference_endpoint'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Inference Endpoint'
								variant='outlined'
								fullWidth
								error={!!errors.inference_endpoint}
								helperText={errors.inference_endpoint?.message}
								disabled={isLoading}
							/>
						)}
					/>

					<Controller
						name='liveness_endpoint'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Liveness Endpoint'
								variant='outlined'
								fullWidth
								error={!!errors.liveness_endpoint}
								helperText={errors.liveness_endpoint?.message}
								disabled={isLoading}
							/>
						)}
					/>

					<Controller
						name='batch_inference_endpoint'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Batch Inference Endpoint'
								variant='outlined'
								fullWidth
								error={!!errors.batch_inference_endpoint}
								helperText={
									errors.batch_inference_endpoint?.message
								}
								disabled={isLoading}
							/>
						)}
					/>

					<Box sx={{ display: 'flex', gap: 2 }}>
						<PrimaryButton
							type='submit'
							variant='contained'
							fullWidth
							disabled={!isDirty || isLoading}
							isLoading={isLoading}
							sx={{ height: '36px' }}
						>
							Create
						</PrimaryButton>

						<Button
							variant='outlined'
							color='error'
							fullWidth
							disabled={!isDirty || isLoading}
							onClick={() => {
								reset();
							}}
						>
							CLEAR
						</Button>
					</Box>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default CreateAIModelModal;
